import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake, snakeToCamel } from "caseparser";

export const accountingsApi = api
  .enhanceEndpoints({ addTagTypes: ["accountings", "agreements"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      currencies: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/accountings/currencies`,
          method: RequestMethod.GET,
          params: camelToSnake({
            branchId,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["accountings"],
      }),
      accountings: builder.query({
        query: ({
          branchId,
          currency,
          minAmount,
          maxAmount,
          personalIds,
          branchIds,
          startDate,
          endDate,
          page,
        }) => ({
          url: `branchs/${branchId}/accountings`,
          method: RequestMethod.GET,
          params: camelToSnake({
            startDate,
            endDate,
            currency,
            minAmount,
            maxAmount,
            personalIds,
            branchIds: branchIds,
            page,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["accountings"],
      }),
      exportAccounting: builder.query({
        query({
          branchId,
          currency,
          minAmount,
          maxAmount,
          personalIds,
          branchIds,
          startDate,
          endDate,
          page,
        }) {
          return {
            url: `branchs/${branchId}/accountings/export`,
            method: RequestMethod.GET,
            responseHandler: async (response) =>
              window.location.assign(
                window.URL.createObjectURL(await response.blob())
              ),
            params: camelToSnake({
              startDate,
              endDate,
              currency,
              minAmount,
              maxAmount,
              personalIds,
              branchIds,
              page,
            }),
            cache: "no-cache",
          };
        },
      }),
      exportSalesAnalysis: builder.query({
        query({
          branchId,
          currency,
          minAmount,
          maxAmount,
          personalIds,
          startDate,
          endDate,
          page,
          branchIds,
        }) {
          return {
            url: `branchs/${branchId}/accountings/export-sales-analysis`,
            method: RequestMethod.GET,
            responseHandler: async (response) => {
              if (!response.ok) {
                const errorResponse = await response.json();
                throw new Error(errorResponse.error || "Error desconocido");
              }

              const blob = await response.blob();

              if (blob.size === 0) {
                throw new Error("No se encontró la información de ventas.");
              }

              const url = window.URL.createObjectURL(blob);
              window.location.assign(url);
            },
            params: camelToSnake({
              startDate,
              endDate,
              currency,
              minAmount,
              maxAmount,
              personalIds,
              page,
              branchIds,
            }),
            cache: "no-cache",
          };
        },
      }),
      agreements: builder.query({
        query: ({ branchId, startDate, endDate }) => ({
          url: `branchs/${branchId}/accountings/agreements`,
          method: RequestMethod.GET,
          params: camelToSnake({
            startDate,
            endDate,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["agreements"],
      }),
      agreementsSummary: builder.query({
        query: ({ branchId, startDate, endDate, tag }) => ({
          url: `branchs/${branchId}/accountings/agreements-summary`,
          method: RequestMethod.POST,
          params: camelToSnake({
            startDate,
            endDate,
            tag,
          }),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["agreements"],
      }),

      importXml: builder.mutation({
        query({ branchId, date, time, file }) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("date", date);
          formData.append("time", time);
          formData.append("branch_id", branchId);

          return {
            url: `branchs/${branchId}/accountings/read-xml`,
            method: RequestMethod.POST,
            body: formData,
            headers: {
              Accept: "application/json",
            },
            responseHandler: async (response) => {
              if (!response.ok) {
                const errorResponse = await response.json();
                throw new Error(errorResponse.error || "Error desconocido");
              }

              return response.json();
            },
          };
        },
        invalidatesTags: ["accountings"],
      }),

      createCreditorSepa: builder.mutation({
        query: ({
          branchId,
          creditorName,
          creditorIban,
          creditorId,
          creditorAgent,
        }) => ({
          url: `branchs/${branchId}/accountings/creditor-store-sepa`,
          method: RequestMethod.POST,
          body: {
            branch_id: branchId,
            name: creditorName,
            iban: creditorIban,
            id_creditor: creditorId,
            bic: creditorAgent,
          },
        }),
        invalidatesTags: ["accountings"],
      }),

      exportXml: builder.query({
        query({ branchId }) {
          return {
            url: `branchs/${branchId}/accountings/exportXml`,
            method: RequestMethod.GET,
            responseHandler: async (response) => {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              window.open(url, "_blank");

              // Opcional: limpiar la URL después de un tiempo
              setTimeout(() => window.URL.revokeObjectURL(url), 100);
            },
            cache: "no-cache",
          };
        },
      }),
      exportDebit: builder.query({
        query({ branchId }) {
          return {
            url: `branchs/${branchId}/accountings/exportExcel`,
            method: RequestMethod.GET,
            responseHandler: async (response) =>
              window.location.assign(
                window.URL.createObjectURL(await response.blob())
              ),
            params: camelToSnake({
              branchId,
            }),
            cache: "no-cache",
          };
        },
      }),
      failedHistory: builder.query({
        query: ({ branchId }) => ({
          url: `branchs/${branchId}/accountings/failed`,
          method: RequestMethod.GET,
          params: camelToSnake({}),
        }),
        transformResponse: (response: any) => snakeToCamel(response.data),
        providesTags: ["accountings"],
      }),
      exportHistory: builder.query({
        query({
          branchId,
          currency,
          minAmount,
          maxAmount,
          personalIds,
          startDate,
          endDate,
          page,
        }) {
          return {
            url: `branchs/${branchId}/accountings/failed-history`,
            method: RequestMethod.GET,
            responseHandler: async (response) =>
              window.location.assign(
                window.URL.createObjectURL(await response.blob())
              ),
            params: camelToSnake({
              startDate,
              endDate,
              currency,
              minAmount,
              maxAmount,
              personalIds,
              page,
            }),
            cache: "no-cache",
          };
        },
      }),
    }),
  });

export const {
  useAccountingsQuery,
  useCurrenciesQuery,
  useLazyExportAccountingQuery,
  useLazyExportSalesAnalysisQuery,
  useAgreementsQuery,
  useAgreementsSummaryQuery,
  useCreateCreditorSepaMutation,
  useImportXmlMutation,
  useLazyExportXmlQuery,
  useLazyExportDebitQuery,
  useLazyExportHistoryQuery,
  useFailedHistoryQuery,
} = accountingsApi;
